import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "motion/react";
import { CHAT_MAX_LENGTH, REACTIONS, type ChatMessage, type PlayerId } from "@minigames/shared";
import { haptic } from "../lib/telegram.js";
import { sound } from "../lib/sound.js";

interface Props {
  messages: ChatMessage[];
  meId: PlayerId;
  opponentName: string;
  onSend(text: string): void;
  onReact(emoji: string): void;
}

/**
 * Общение в партии: реакции одним тапом и разворачиваемый чат.
 *
 * Реакции стоят первым рядом намеренно — они закрывают большую часть
 * потребности «отреагировать на ход» и, в отличие от свободного текста,
 * безопасны со случайным соперником.
 */
export function ChatPanel({ messages, meId, opponentName, onSend, onReact }: Props) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [seen, setSeen] = useState(0);
  const listRef = useRef<HTMLDivElement>(null);

  const unread = messages.filter((m) => m.by !== meId).length - seen;

  useEffect(() => {
    if (open) {
      setSeen(messages.filter((m) => m.by !== meId).length);
      listRef.current?.scrollTo({ top: listRef.current.scrollHeight, behavior: "smooth" });
    }
  }, [open, messages, meId]);

  function send() {
    const value = text.trim();
    if (!value) return;
    onSend(value.slice(0, CHAT_MAX_LENGTH));
    setText("");
    haptic.tap();
  }

  return (
    <div className="chat">
      <div className="chat-reactions">
        {REACTIONS.map((emoji) => (
          <motion.button
            key={emoji}
            className="chat-reaction"
            onClick={() => {
              haptic.select();
              sound.select();
              onReact(emoji);
            }}
            whileTap={{ scale: 0.85 }}
          >
            {emoji}
          </motion.button>
        ))}
        <button
          className={`chat-toggle${unread > 0 ? " has-unread" : ""}`}
          onClick={() => {
            haptic.tap();
            setOpen((v) => !v);
          }}
        >
          💬
          {unread > 0 && <span className="chat-badge">{unread > 9 ? "9+" : unread}</span>}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            className="chat-body"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
          >
            <div className="chat-list" ref={listRef}>
              {messages.length === 0 ? (
                <div className="muted small" style={{ padding: "8px 4px" }}>
                  Напиши что-нибудь сопернику
                </div>
              ) : (
                messages.map((m) => (
                  <div key={m.id} className={`chat-msg${m.by === meId ? " is-mine" : ""}`}>
                    <span className="chat-author muted small">
                      {m.by === meId ? "Я" : opponentName}
                    </span>
                    <span>{m.text}</span>
                  </div>
                ))
              )}
            </div>

            <div className="chat-input">
              <input
                value={text}
                onChange={(e) => setText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") send();
                }}
                maxLength={CHAT_MAX_LENGTH}
                placeholder="Сообщение…"
                inputMode="text"
              />
              <button className="chat-send" onClick={send} disabled={!text.trim()}>
                ➤
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

/** Реакция, всплывающая над аватаркой отправителя. */
export function FloatingReaction({ emoji }: { emoji: string }) {
  return (
    <motion.span
      className="floating-reaction"
      initial={{ opacity: 0, y: 4, scale: 0.5 }}
      animate={{ opacity: 1, y: -26, scale: 1.15 }}
      exit={{ opacity: 0, y: -44, scale: 0.9 }}
      transition={{ type: "spring", stiffness: 380, damping: 22 }}
    >
      {emoji}
    </motion.span>
  );
}
